import React from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../../../../components/ui/Button';
import { Skeleton } from '../../../../components/ui/Skeleton';
import ReminderTemplateSelect from './ReminderTemplateSelect';
import { findTemplate } from '../utils/reminderTemplates';

/**
 * Nombre de la plantilla por defecto de un canal, para rotular el selector. `null` si el id
 * configurado no está entre las activas (no se inventa un nombre).
 */
function defaultTemplateLabel(t, catalog, defaultId) {
  if (!defaultId) return t('Plantilla del sistema');
  return findTemplate(catalog, defaultId)?.name ?? null;
}

/**
 * Plantilla de cada canal elegido en "Enviar recordatorio" y "Programar". Arranca en la
 * plantilla por defecto del operador; `null` es la del sistema.
 *
 * Sin catálogo (cargando o falló) no se muestran selectores: la elección sigue siendo la
 * por defecto y así se manda. Un id que billing rechazó (archivado o de otro canal) se marca
 * en su selector con `invalidChannels`.
 *
 * @param {{ channels: ('email'|'whatsapp')[], value: { email: string|null, whatsapp: string|null },
 *   onChange: (channel: 'email'|'whatsapp', templateId: string|null) => void,
 *   templates: ReturnType<typeof import('../hooks/useReminderTemplates').default>,
 *   defaults: { email: string|null, whatsapp: string|null },
 *   invalidChannels: ('email'|'whatsapp')[], disabled?: boolean }} props
 */
export default function ReminderTemplateFields({
  channels,
  value,
  onChange,
  templates,
  defaults,
  invalidChannels,
  disabled,
}) {
  const { t } = useTranslation();
  if (channels.length === 0) return null;
  const { catalog } = templates;

  if (!catalog) {
    if (templates.loading) {
      return (
        <div className="space-y-2" role="status" aria-live="polite">
          <span className="sr-only">{t('Cargando plantillas…')}</span>
          <Skeleton className="h-10 w-full" />
        </div>
      );
    }
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-md bg-status-warning-soft px-3 py-2 text-sm text-status-warning" role="alert">
        <span>{t('No se pudieron cargar las plantillas: se usa la plantilla por defecto de cada canal.')}</span>
        <Button size="sm" variant="outline" onClick={templates.reload}>
          {t('Reintentar')}
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {channels.map((channel) => {
        const defaultLabel = defaultTemplateLabel(t, catalog, defaults?.[channel] ?? null);
        return (
          <ReminderTemplateSelect
            key={channel}
            label={channel === 'email' ? t('Plantilla de correo') : t('Plantilla de WhatsApp')}
            channel={channel}
            value={value?.[channel] ?? null}
            onChange={(templateId) => onChange(channel, templateId)}
            catalog={catalog}
            nullLabel={t('Plantilla del sistema')}
            disabled={disabled}
            error={
              invalidChannels.includes(channel)
                ? t('Esta plantilla ya no está disponible: se archivó o es de otro canal. Elegí otra.')
                : undefined
            }
            // El nombre es texto del operador: fuera de t().
            hint={defaultLabel ? `${t('Por defecto')}: ${defaultLabel}` : undefined}
          />
        );
      })}
    </div>
  );
}
